import { type QueryClient, useQuery } from '@tanstack/react-query';

import { getCurrentUser } from '@/services/user';

export type User = {
  country_code: string | null;
  created_at: string;
  email: string;
  first_name: string;
  last_name: string | null;
  phone: string;
  status: 'ACTIVE' | 'INACTIVE';
  tenant_id: string;
  updated_at: string;
  user_id: string;
  has_account: boolean;
  has_seen_credits_tour?: boolean;
  phone_undeliverable_at?: string | null;
  email_undeliverable_at?: string | null;
};

// Shared with every writer, including the HTTP adapter's session-ended path:
// a literal there that drifts from the one this hook reads would leave the app
// rendering a signed-in user against a session that is gone, with no type error
// to catch it.
export const USER_QUERY_KEY = ['user'];

// A focus within this of the last check does not ask again.
const SESSION_STALE_MS = 60_000;

// A login, signup or email change answers the session's fields only, without
// the undeliverable flags, so the user is shown at once and read again from
// /users/me behind it, with no loading state.
export const setSignedInUser = (
  queryClient: QueryClient,
  user: User | undefined
) => {
  queryClient.setQueryData(USER_QUERY_KEY, user);
  void queryClient.invalidateQueries({ queryKey: USER_QUERY_KEY });
};

export default function useUser() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: USER_QUERY_KEY,
    queryFn: () => getCurrentUser(),
    retry: false,
    // A tab or a Home Screen app can sit for days and come back with an
    // expired access cookie. Asking again on return lets the GET refresh it
    // before the visitor's first booking, a POST, which never refreshes. A
    // signed-out visitor has no session to refresh.
    refetchOnWindowFocus: (query) => !!query.state.data,
    staleTime: SESSION_STALE_MS,
    // The session check answers who the visitor is, never whether the studio
    // is open, so its failure must not be what turns the whole site dark. Read
    // by the cache-level failure handler in lib/query-client.ts.
    meta: { isSessionCheck: true },
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
}
